import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_BASE || '';

const fmt = (v) =>
  v == null ? '—' : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

// Dias em aberto desde a data da auditoria ("dd/mm/aaaa hh:mm") até hoje.
const diasEmAberto = (dataAuditoria) => {
  const dataStr = (dataAuditoria || '').split(' ')[0]; // dd/mm/aaaa
  const [d, m, y] = dataStr.split('/').map(Number);
  if (!d || !m || !y) return null;
  const inicio = new Date(y, m - 1, d);
  const hoje = new Date();
  // zera horas para contar dias inteiros
  inicio.setHours(0, 0, 0, 0);
  hoje.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((hoje - inicio) / 86400000));
};

const LIMITE_URGENTE = 30; // dias em aberto a partir do qual fica Urgente

// Chave de identidade de um item de auditoria: Conta + Descrição (normalizadas).
// Usada para detectar itens recorrentes entre auditorias.
const chaveItem = (it) => {
  const norm = (s) => (s || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
  return `${norm(it?.conta)}||${norm(it?.descricao)}`;
};

const SevBadge = ({ sev }) => {
  const s = sev?.toUpperCase();
  if (s === 'GRAVE') return <span className="badge bg-danger">Grave</span>;
  if (s === 'MEDIO') return <span className="badge bg-warning text-dark">Médio</span>;
  if (s === 'BAIXO') return <span className="badge bg-info text-dark">Baixo</span>;
  return <span className="badge bg-secondary">{sev || '—'}</span>;
};

const ConferBadge = ({ val }) => {
  const v = (val || '').toLowerCase();
  if (v === 'confirmado') return <span className="badge bg-success">Confirmado</span>;
  if (v === 'contestado') return <span className="badge bg-danger">Contestado</span>;
  return <span className="text-muted small">—</span>;
};

// ── Popup para registrar como o item foi resolvido ─────────────────────────────
const PopupResolucao = ({ item, onConfirmar, onCancelar, salvando }) => {
  const [texto, setTexto] = useState('');
  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1060 }}
      onClick={onCancelar}>
      <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header" style={{ backgroundColor: '#fff', borderBottom: '1px solid #e9ecef' }}>
            <div>
              <h6 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                <i className="fas fa-clipboard-check me-2 text-success"></i>Registrar resolução
              </h6>
              <small style={{ color: '#6c757d' }}>{item.conta || '—'} · {item.categoria || '—'}</small>
            </div>
            <button type="button" className="btn-close" onClick={onCancelar}></button>
          </div>
          <div className="modal-body">
            <label className="form-label fw-semibold small">Como este item foi resolvido? <span className="text-danger">*</span></label>
            <textarea className="form-control" rows={4} autoFocus
              placeholder="Descreva o que foi feito para resolver este apontamento..."
              value={texto} onChange={e => setTexto(e.target.value)} />
            <small className="text-muted d-block mt-2">
              <i className="fas fa-info-circle me-1"></i>
              Ficará registrado com seu nome, data e hora — visível ao passar o mouse na coluna Resolvido.
            </small>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary btn-sm" onClick={onCancelar} disabled={salvando}>
              <i className="fas fa-times me-1"></i>Cancelar
            </button>
            <button className="btn btn-success btn-sm" disabled={salvando || !texto.trim()}
              onClick={() => onConfirmar(texto.trim())}>
              {salvando
                ? <><span className="spinner-border spinner-border-sm me-1"></span>Salvando...</>
                : <><i className="fas fa-check me-1"></i>Marcar como resolvido</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Popup para colocar o item EM ANDAMENTO (aguardando o cliente) ─────────────
const PopupAndamento = ({ item, onConfirmar, onCancelar, salvando }) => {
  const [texto, setTexto] = useState('');
  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1060 }}
      onClick={onCancelar}>
      <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header" style={{ backgroundColor: '#fff', borderBottom: '1px solid #e9ecef' }}>
            <div>
              <h6 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                <i className="fas fa-hourglass-half me-2" style={{ color: '#fd7e14' }}></i>
                Colocar em acompanhamento
              </h6>
              <small style={{ color: '#6c757d' }}>{item.conta || '—'} · {item.categoria || '—'}</small>
            </div>
            <button type="button" className="btn-close" onClick={onCancelar}></button>
          </div>
          <div className="modal-body">
            <label className="form-label fw-semibold small">
              O que está sendo aguardado do cliente? <span className="text-danger">*</span>
            </label>
            <textarea className="form-control" rows={4} autoFocus
              placeholder="Ex.: solicitado ao cliente o comprovante da transferência; aguardando retorno."
              value={texto} onChange={e => setTexto(e.target.value)} />
            <div className="alert alert-warning py-2 px-3 mt-3 mb-0 small">
              <i className="fas fa-info-circle me-1"></i>
              O item <strong>sai das pendências de auditoria</strong> nesta competência e
              volta a ser cobrado automaticamente na próxima.
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary btn-sm" onClick={onCancelar} disabled={salvando}>
              <i className="fas fa-times me-1"></i>Cancelar
            </button>
            <button className="btn btn-sm text-white" disabled={salvando || !texto.trim()}
              style={{ backgroundColor: '#fd7e14', borderColor: '#fd7e14' }}
              onClick={() => onConfirmar(texto.trim())}>
              {salvando
                ? <><span className="spinner-border spinner-border-sm me-1"></span>Salvando...</>
                : <><i className="fas fa-hourglass-half me-1"></i>Marcar em andamento</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Confirmação de exclusão da auditoria ─────────────────────────────────────
// Ação definitiva: mostra exatamente o que será apagado antes de liberar o
// botão. Sem window.confirm — o usuário precisa ler o que está excluindo.
const PopupExcluir = ({ audit, onConfirmar, onCancelar, excluindo, erro }) => {
  if (!audit) return null;
  const itens       = audit.itens || [];
  const resolvidos  = itens.filter(i => i.resolvido).length;
  const confirmados = itens.filter(i => (i.conferencia || '').toLowerCase() === 'confirmado').length;

  const Linha = ({ rotulo, valor }) => (
    <div className="d-flex justify-content-between py-1">
      <span style={{ color: '#6c757d' }}>{rotulo}</span>
      <span className="fw-semibold text-end" style={{ color: '#212529' }}>{valor || '—'}</span>
    </div>
  );

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1070 }}
      onClick={excluindo ? undefined : onCancelar}>
      <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header" style={{ backgroundColor: '#fff', borderBottom: '1px solid #e9ecef' }}>
            <div>
              <h6 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                <i className="fas fa-trash-alt me-2" style={{ color: '#dc3545' }}></i>
                Excluir auditoria
              </h6>
              <small style={{ color: '#6c757d' }}>Esta ação não pode ser desfeita</small>
            </div>
            <button type="button" className="btn-close" onClick={onCancelar} disabled={excluindo}></button>
          </div>
          <div className="modal-body">
            <div className="border rounded p-3 small" style={{ backgroundColor: '#f8f9fa' }}>
              <Linha rotulo="Cliente" valor={audit.cliente} />
              <Linha rotulo="Nº Domínio" valor={audit.numero_dominio} />
              <Linha rotulo="Competência" valor={audit.periodo} />
              <Linha rotulo="Data da auditoria" valor={audit.data_auditoria} />
              <Linha rotulo="Operador" valor={audit.operador_empresa} />
            </div>
            <div className="alert alert-danger py-2 px-3 mt-3 mb-0 small">
              <i className="fas fa-exclamation-triangle me-1"></i>
              Serão apagados <strong>{itens.length} item(ns)</strong> do histórico
              {confirmados > 0 && <> — <strong>{confirmados}</strong> confirmado(s) pelo operador</>}
              {resolvidos > 0 && <> e <strong>{resolvidos}</strong> já resolvido(s)</>}.
              Os apontamentos também deixam de contar no relatório de auditorias.
            </div>
            {erro && (
              <div className="alert alert-warning py-2 px-3 mt-2 mb-0 small">
                <i className="fas fa-times-circle me-1"></i>{erro}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary btn-sm" onClick={onCancelar} disabled={excluindo}>
              <i className="fas fa-times me-1"></i>Cancelar
            </button>
            <button className="btn btn-danger btn-sm" onClick={onConfirmar} disabled={excluindo}>
              {excluindo
                ? <><span className="spinner-border spinner-border-sm me-1"></span>Excluindo...</>
                : <><i className="fas fa-trash-alt me-1"></i>Excluir definitivamente</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Modal de itens ────────────────────────────────────────────────────────────
const ModalItens = ({ audit, auditorias, onClose, onToggleItem, onToggleAndamento, podeEditarPlano, onEditarPlano }) => {
  const [togglingItemId, setTogglingItemId] = useState(null);
  const [popupItem, setPopupItem]           = useState(null); // item aguardando descrição de resolução
  const [salvandoPopup, setSalvandoPopup]   = useState(false);
  const [popupAndamento, setPopupAndamento] = useState(null); // item indo para acompanhamento
  const [salvandoAndamento, setSalvandoAndamento] = useState(false);
  const [editandoPlanoId, setEditandoPlanoId] = useState(null); // item com plano em edição
  const [planoDraft, setPlanoDraft]           = useState('');   // texto em edição
  const [salvandoPlano, setSalvandoPlano]     = useState(false);

  // Chaves (Conta + Descrição) de itens apontados em auditorias ANTERIORES
  // do mesmo cliente — para sinalizar recorrência (não resolvido antes).
  const itensRecorrentes = useMemo(() => {
    const set = new Set();
    if (!audit) return set;
    const nd = String(audit.numero_dominio || '');
    const refIso = audit.data_iso || '';
    (auditorias || []).forEach(a => {
      if (a.auditoria_id === audit.auditoria_id) return;
      if (String(a.numero_dominio || '') !== nd) return;
      // só auditorias estritamente anteriores (ambas as datas precisam existir)
      if (!a.data_iso || !refIso || a.data_iso >= refIso) return;
      (a.itens || []).forEach(it => set.add(chaveItem(it)));
    });
    return set;
  }, [audit, auditorias]);

  if (!audit) return null;

  const totalItens     = audit.itens.length;
  const totalResolvidos = audit.itens.filter(i => i.resolvido).length;
  const totalEmAndamento = audit.itens.filter(i => i.em_andamento && !i.resolvido).length;
  const diasAudit      = diasEmAberto(audit.data_auditoria);
  const urgenteAudit   = diasAudit != null && diasAudit > LIMITE_URGENTE;
  const qtdRecorrentes = audit.itens.filter(it => itensRecorrentes.has(chaveItem(it))).length;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }} onClick={onClose}>
      <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
        style={{ maxWidth: '98vw', width: '98vw' }} onClick={e => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header"
            style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
            <div>
              <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                <i className="fas fa-search me-2"></i>
                {audit.cliente || 'Sem nome'} — {audit.periodo || '—'}
              </h5>
              <small style={{ color: '#6c757d' }}>
                Nº {audit.numero_dominio || '—'} · {audit.regime || '—'} · Operador da empresa: <strong>{audit.operador_empresa || '—'}</strong> · Auditor: {audit.operador} · {audit.data_auditoria}
              </small>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-body p-0">
            <div className="d-flex gap-2 flex-wrap align-items-center p-3 bg-light border-bottom">
              {audit.totais.grave  > 0 && <span className="badge bg-danger fs-6">{audit.totais.grave} Grave{audit.totais.grave > 1 ? 's' : ''}</span>}
              {audit.totais.medio  > 0 && <span className="badge bg-warning text-dark fs-6">{audit.totais.medio} Médio{audit.totais.medio > 1 ? 's' : ''}</span>}
              {audit.totais.baixo  > 0 && <span className="badge bg-info text-dark fs-6">{audit.totais.baixo} Baixo{audit.totais.baixo > 1 ? 's' : ''}</span>}
              {audit.totais.manual > 0 && <span className="badge bg-secondary fs-6">{audit.totais.manual} Manual{audit.totais.manual > 1 ? 'is' : ''}</span>}
              {qtdRecorrentes > 0 && (
                <span className="badge fs-6" style={{ background: '#6f42c1', color: '#fff' }}
                  title="Itens já apontados em auditoria anterior deste cliente">
                  <i className="fas fa-history me-1"></i>
                  {qtdRecorrentes} recorrente{qtdRecorrentes > 1 ? 's' : ''} de auditoria anterior
                </span>
              )}
              {totalEmAndamento > 0 && (
                <span className="badge fs-6 text-white" style={{ backgroundColor: '#fd7e14' }}
                  title="Itens aguardando retorno do cliente — fora das pendências nesta competência">
                  <i className="fas fa-hourglass-half me-1"></i>
                  {totalEmAndamento} em andamento
                </span>
              )}
              <span className={`ms-auto badge fs-6 ${totalResolvidos === totalItens ? 'bg-success' : 'bg-secondary'}`}>
                <i className="fas fa-check-double me-1"></i>
                {totalResolvidos}/{totalItens} resolvidos
              </span>
              {!audit.resolvido && diasAudit != null && (
                <span className={`badge fs-6 ${urgenteAudit ? 'bg-danger' : 'bg-warning text-dark'}`}>
                  <i className={`fas ${urgenteAudit ? 'fa-exclamation-triangle' : 'fa-hourglass-half'} me-1`}></i>
                  {diasAudit} dia{diasAudit !== 1 ? 's' : ''} em aberto{urgenteAudit ? ' · URGENTE' : ''}
                </span>
              )}
            </div>

            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr className="text-center">
                    <th>Severidade</th>
                    <th className="text-start">Categoria</th>
                    <th>Cód.</th>
                    <th className="text-start">Conta</th>
                    <th>Valor</th>
                    <th className="text-start" style={{ minWidth: 160 }}>Descrição</th>
                    <th className="text-start" style={{ minWidth: 150 }}>Recomendação</th>
                    <th>Conferência</th>
                    <th className="text-start" style={{ minWidth: 130 }}>Plano de Resolução</th>
                    <th>Resolvido</th>
                    {/* verde OPACO: o thead do .das-page é azul (#0073e6); um verde translúcido deixaria o azul passar */}
                    <th className="text-start" style={{ minWidth: 200, backgroundColor: '#d4edda', color: '#1e7e34' }}>Como foi resolvido</th>
                    <th>Dias em Aberto</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.itens.map((item) => {
                    const recorrente = itensRecorrentes.has(chaveItem(item));
                    return (
                    <tr key={item.id} style={
                      item.resolvido ? { background: '#f0fff4' }
                        : item.em_andamento ? { background: '#fff8f0' } : {}
                    }>
                      <td className="text-center">
                        <SevBadge sev={item.severidade} />
                        {item.item_checklist === 'M' && <span className="badge bg-secondary ms-1" style={{ fontSize: '0.68rem' }}>Manual</span>}
                      </td>
                      <td style={{ fontSize: '0.85rem' }}>{item.categoria || '—'}</td>
                      <td className="text-center font-monospace text-muted" style={{ fontSize: '0.82rem' }}>{item.codigo || '—'}</td>
                      <td className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                        {item.conta || '—'}
                        {recorrente && (
                          <i className="fas fa-history ms-2"
                            style={{ color: '#6f42c1', cursor: 'help' }}
                            title="Este item já foi apontado em uma auditoria anterior deste cliente e, pelo visto, ainda não foi resolvido."></i>
                        )}
                      </td>
                      <td className="text-end" style={{ fontSize: '0.85rem' }}>{item.valor != null ? fmt(item.valor) : '—'}</td>
                      <td className="text-muted" style={{ fontSize: '0.82rem' }}>{item.descricao || '—'}</td>
                      <td className="text-muted fst-italic" style={{ fontSize: '0.82rem' }}>{item.recomendacao || '—'}</td>
                      <td className="text-center"><ConferBadge val={item.conferencia} /></td>
                      <td style={{ fontSize: '0.80rem', minWidth: 180 }}>
                        {editandoPlanoId === item.id ? (
                          <div>
                            <textarea className="form-control form-control-sm" rows={3} autoFocus
                              value={planoDraft} onChange={e => setPlanoDraft(e.target.value)}
                              style={{ fontSize: '0.80rem' }} disabled={salvandoPlano} />
                            <div className="d-flex gap-1 mt-1">
                              <button className="btn btn-success btn-sm py-0 px-2" disabled={salvandoPlano}
                                style={{ fontSize: '0.72rem' }}
                                onClick={() => {
                                  setSalvandoPlano(true);
                                  onEditarPlano(item.id, audit.auditoria_id, planoDraft.trim())
                                    .then(() => setEditandoPlanoId(null))
                                    .finally(() => setSalvandoPlano(false));
                                }}>
                                {salvandoPlano
                                  ? <span className="spinner-border spinner-border-sm" />
                                  : <><i className="fas fa-check me-1"></i>Salvar</>}
                              </button>
                              <button className="btn btn-outline-secondary btn-sm py-0 px-2" disabled={salvandoPlano}
                                style={{ fontSize: '0.72rem' }}
                                onClick={() => setEditandoPlanoId(null)}>
                                Cancelar
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="d-flex align-items-start gap-1">
                            <span className="flex-grow-1">{item.plano_resolucao || <span className="text-muted">—</span>}</span>
                            {podeEditarPlano && (
                              <button className="btn btn-link btn-sm p-0 text-primary" title="Editar plano de resolução"
                                style={{ fontSize: '0.75rem', lineHeight: 1 }}
                                onClick={() => { setEditandoPlanoId(item.id); setPlanoDraft(item.plano_resolucao || ''); }}>
                                <i className="fas fa-pen"></i>
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="text-center">
                        <button
                          className={`btn btn-sm ${item.resolvido ? 'btn-success' : 'btn-outline-secondary'}`}
                          style={{ fontSize: '0.72rem', padding: '2px 8px', whiteSpace: 'nowrap', cursor: 'help' }}
                          disabled={togglingItemId === item.id}
                          title={item.resolvido && item.resolvido_por
                            ? `Resolvido por ${item.resolvido_por}${item.resolvido_em ? ' em ' + item.resolvido_em : ''}\n\n${item.resolvido_obs || ''}`
                            : (item.resolvido ? 'Resolvido' : 'Marcar como resolvido')}
                          onClick={() => {
                            if (item.resolvido) {
                              // desmarcar: volta para "Não" e limpa o registro
                              setTogglingItemId(item.id);
                              onToggleItem(item.id, false, audit.auditoria_id)
                                .finally(() => setTogglingItemId(null));
                            } else {
                              // marcar: abre o popup para descrever a resolução
                              setPopupItem(item);
                            }
                          }}
                        >
                          {togglingItemId === item.id
                            ? <span className="spinner-border spinner-border-sm" />
                            : item.resolvido
                              ? <><i className="fas fa-check me-1"></i>Sim{item.resolvido_obs ? <i className="fas fa-comment-dots ms-1" style={{ fontSize: '0.68rem' }}></i> : null}</>
                              : <><i className="fas fa-clock me-1"></i>Não</>
                          }
                        </button>

                        {/* EM ANDAMENTO: item já tratado, aguardando o cliente.
                            Sai das pendências nesta competência e volta na próxima. */}
                        {!item.resolvido && (
                          <button
                            className={`btn btn-sm mt-1 d-block w-100 ${item.em_andamento ? 'text-white' : 'btn-outline-warning'}`}
                            style={{
                              fontSize: '0.68rem', padding: '2px 6px', whiteSpace: 'nowrap',
                              ...(item.em_andamento ? { backgroundColor: '#fd7e14', borderColor: '#fd7e14' } : {})
                            }}
                            disabled={togglingItemId === item.id}
                            title={item.em_andamento
                              ? `Em acompanhamento desde ${item.andamento_em || '—'} (${item.andamento_competencia})\nPor ${item.andamento_por || '—'}\n\n${item.andamento_obs || ''}\n\nClique para retirar do acompanhamento.`
                              : 'Depende de retorno do cliente: sai das pendências nesta competência'}
                            onClick={() => {
                              if (item.em_andamento) {
                                setTogglingItemId(item.id);
                                onToggleAndamento(item.id, false, audit.auditoria_id)
                                  .finally(() => setTogglingItemId(null));
                              } else {
                                setPopupAndamento(item);
                              }
                            }}
                          >
                            <i className="fas fa-hourglass-half me-1"></i>
                            {item.em_andamento ? 'Em andamento' : 'Em andamento?'}
                          </button>
                        )}
                        {item.andamento_expirado && !item.resolvido && (
                          <div className="text-danger mt-1" style={{ fontSize: '0.66rem', lineHeight: 1.2 }}
                            title={`Acompanhamento marcado em ${item.andamento_competencia} expirou na virada da competência.`}>
                            <i className="fas fa-rotate-left me-1"></i>
                            Voltou de {item.andamento_competencia}
                          </div>
                        )}
                      </td>
                      <td style={{ fontSize: '0.80rem', backgroundColor: 'rgba(40,167,69,0.08)' }}>
                        {item.resolvido && item.resolvido_obs ? (
                          <div>
                            <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.4, color: '#1e7e34' }}>{item.resolvido_obs}</div>
                            <div className="text-muted mt-1" style={{ fontSize: '0.70rem' }}>
                              <i className="fas fa-user-check me-1"></i>
                              {item.resolvido_por || '—'}
                              {item.resolvido_em && <> · {item.resolvido_em}</>}
                            </div>
                          </div>
                        ) : item.andamento_obs ? (
                          // acompanhamento: o que está sendo aguardado do cliente
                          <div>
                            <div className="fw-semibold" style={{ fontSize: '0.72rem', color: '#fd7e14' }}>
                              <i className="fas fa-hourglass-half me-1"></i>
                              {item.em_andamento
                                ? `Em acompanhamento (${item.andamento_competencia})`
                                : `Acompanhamento encerrado em ${item.andamento_competencia}`}
                            </div>
                            <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.4, color: '#8a4b08' }}>{item.andamento_obs}</div>
                            <div className="text-muted mt-1" style={{ fontSize: '0.70rem' }}>
                              <i className="fas fa-user-clock me-1"></i>
                              {item.andamento_por || '—'}
                              {item.andamento_em && <> · {item.andamento_em}</>}
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      {/* background transparent cancela a regra global .table td:nth-child(12) (verde da coluna "valor" em outra tela) */}
                      <td className="text-center" style={{ backgroundColor: 'transparent' }}>
                        {item.resolvido || diasAudit == null ? (
                          <span className="text-muted small">—</span>
                        ) : (
                          <span className={`badge ${urgenteAudit ? 'bg-danger' : 'bg-warning text-dark'}`}
                            style={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                            {urgenteAudit && <i className="fas fa-exclamation-triangle me-1"></i>}
                            {diasAudit} dia{diasAudit !== 1 ? 's' : ''}{urgenteAudit ? ' · Urgente' : ''}
                          </span>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              <i className="fas fa-times me-1"></i>Fechar
            </button>
          </div>
        </div>
      </div>

      {popupItem && (
        <PopupResolucao
          item={popupItem}
          salvando={salvandoPopup}
          onCancelar={() => { if (!salvandoPopup) setPopupItem(null); }}
          onConfirmar={(obs) => {
            setSalvandoPopup(true);
            setTogglingItemId(popupItem.id);
            onToggleItem(popupItem.id, true, audit.auditoria_id, obs)
              .then(() => setPopupItem(null))
              .finally(() => { setSalvandoPopup(false); setTogglingItemId(null); });
          }}
        />
      )}

      {popupAndamento && (
        <PopupAndamento
          item={popupAndamento}
          salvando={salvandoAndamento}
          onCancelar={() => { if (!salvandoAndamento) setPopupAndamento(null); }}
          onConfirmar={(obs) => {
            setSalvandoAndamento(true);
            setTogglingItemId(popupAndamento.id);
            onToggleAndamento(popupAndamento.id, true, audit.auditoria_id, obs)
              .then(() => setPopupAndamento(null))
              .finally(() => { setSalvandoAndamento(false); setTogglingItemId(null); });
          }}
        />
      )}
    </div>
  );
};

// ── Página principal ──────────────────────────────────────────────────────────
export default function Historico_auditoria() {
  const [auditorias, setAuditorias]           = useState([]);
  const [carregando, setCarregando]           = useState(true);
  const [erro, setErro]                       = useState(null);
  const [auditoriaSelecionada, setAuditoriaSelecionada] = useState(null);
  const [username, setUsername]               = useState('usuário');
  const [userId, setUserId]                   = useState(null);
  const [ehAdm, setEhAdm]                     = useState(false);
  const [filtros, setFiltros]                 = useState({ busca: '', operador: '', competencia: '', dataGeracao: '', apenasContestadas: false });
  const [auditoriaParaExcluir, setAuditoriaParaExcluir] = useState(null);
  // Visão "Por item": todos os itens auditados numa tabela só, sem abrir
  // auditoria por auditoria. A escolha fica guardada no navegador.
  const [visao, setVisao] = useState(() => {
    try { return localStorage.getItem('hist_auditoria_visao') || 'itens'; } catch (e) { return 'itens'; }
  });
  const trocarVisao = (v) => {
    setVisao(v);
    try { localStorage.setItem('hist_auditoria_visao', v); } catch (e) { /* sem storage, segue */ }
  };
  const [filtroItem, setFiltroItem] = useState({ severidade: '', situacao: 'pendentes', texto: '', dataDe: '', dataAte: '', conferencia: '' });
  const [excluindo, setExcluindo]             = useState(false);
  const [erroExcluir, setErroExcluir]         = useState(null);

  // Identifica o operador logado e se é do grupo admin (mesmo padrão da Contabilidade.js)
  useEffect(() => {
    const token = localStorage.getItem('access');
    axios.get(`${API}/api/verificar-grupo/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (r.data.success) {
          setUsername(r.data.name || r.data.first_name || r.data.usuario || 'usuário');
          setUserId(r.data.user_id ?? null);
          // Admin = grupo 'adm' OU superusuário do Django (ex.: Giovani, que é
          // superuser mas está no grupo 'contabil'). Ambos veem todas as auditorias.
          const grupoAdm = (r.data.grupo || '').toLowerCase().trim() === 'adm';
          setEhAdm(grupoAdm || r.data.is_superuser === true);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('access');
    setCarregando(true);
    axios.get(`${API}/api/listar-historico-auditoria/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r  => { setAuditorias(r.data.auditorias || []); setErro(null); })
      .catch(() => setErro('Não foi possível carregar o histórico.'))
      .finally(() => setCarregando(false));
  }, []);

  const handleToggleItem = async (item_id, novoValor, auditoria_id, obs = '') => {
    const token = localStorage.getItem('access');
    const res = await axios.post(`${API}/api/toggle-resolvido-item/`,
      { item_id, resolvido: novoValor, resolvido_obs: obs },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const { auditoria_resolvida, auditoria_pendente, resolvido_obs, resolvido_por, resolvido_em } = res.data;

    const aplicar = (i) => i.id === item_id
      ? { ...i, resolvido: novoValor,
          // resolver encerra o acompanhamento (o backend também limpa a flag)
          em_andamento: novoValor ? false : i.em_andamento,
          resolvido_obs: resolvido_obs || '', resolvido_por: resolvido_por || '', resolvido_em: resolvido_em || '' }
      : i;

    setAuditorias(prev => prev.map(a =>
      a.auditoria_id !== auditoria_id ? a
        : { ...a, itens: a.itens.map(aplicar), resolvido: auditoria_resolvida, pendente: auditoria_pendente }));

    setAuditoriaSelecionada(prev =>
      (!prev || prev.auditoria_id !== auditoria_id) ? prev
        : { ...prev, itens: prev.itens.map(aplicar), resolvido: auditoria_resolvida, pendente: auditoria_pendente });
  };

  // Coloca/retira o item do acompanhamento (EM ANDAMENTO). Item em andamento
  // sai das pendências na competência atual e volta a ser cobrado na próxima.
  const handleToggleAndamento = async (item_id, novoValor, auditoria_id, obs = '') => {
    const token = localStorage.getItem('access');
    const res = await axios.post(`${API}/api/toggle-andamento-item/`,
      { item_id, andamento: novoValor, andamento_obs: obs },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const {
      em_andamento, andamento_obs, andamento_por, andamento_em,
      andamento_competencia, auditoria_pendente,
    } = res.data;

    const aplicar = (i) => i.id === item_id
      ? { ...i,
          em_andamento: !!em_andamento,
          andamento_marcado: novoValor,
          andamento_expirado: false,
          andamento_obs: andamento_obs || '',
          andamento_por: andamento_por || '',
          andamento_em: andamento_em || '',
          andamento_competencia: andamento_competencia || '' }
      : i;

    setAuditorias(prev => prev.map(a =>
      a.auditoria_id !== auditoria_id ? a
        : { ...a, itens: a.itens.map(aplicar), pendente: auditoria_pendente,
            em_andamento: a.itens.map(aplicar).filter(i => i.em_andamento && !i.resolvido).length }));

    setAuditoriaSelecionada(prev =>
      (!prev || prev.auditoria_id !== auditoria_id) ? prev
        : { ...prev, itens: prev.itens.map(aplicar), pendente: auditoria_pendente });
  };

  // Exclui a auditoria inteira (todos os itens do mesmo auditoria_id).
  // Só admin vê o botão; a autorização real é no backend.
  const handleExcluirAuditoria = async () => {
    if (!auditoriaParaExcluir) return;
    const id = auditoriaParaExcluir.auditoria_id;
    setExcluindo(true);
    setErroExcluir(null);
    try {
      const token = localStorage.getItem('access');
      await axios.post(`${API}/api/excluir-auditoria/`, { auditoria_id: id },
        { headers: { Authorization: `Bearer ${token}` } });
      setAuditorias(prev => prev.filter(a => a.auditoria_id !== id));
      // se o modal de itens estava aberto nesta auditoria, fecha junto
      setAuditoriaSelecionada(prev => (prev && prev.auditoria_id === id ? null : prev));
      setAuditoriaParaExcluir(null);
    } catch (e) {
      setErroExcluir(e?.response?.data?.erro || 'Não foi possível excluir a auditoria.');
    } finally {
      setExcluindo(false);
    }
  };

  // IDs autorizados a editar o Plano de Resolução (checagem real é no backend).
  const IDS_EDITAM_PLANO = [49, 9, 2];
  const podeEditarPlano = userId != null && IDS_EDITAM_PLANO.includes(Number(userId));

  const handleEditarPlano = async (item_id, auditoria_id, plano) => {
    const token = localStorage.getItem('access');
    await axios.post(`${API}/api/editar-plano-resolucao-item/`,
      { item_id, plano_resolucao: plano },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const aplicar = (i) => i.id === item_id ? { ...i, plano_resolucao: plano } : i;
    setAuditorias(prev => prev.map(a =>
      a.auditoria_id !== auditoria_id ? a : { ...a, itens: a.itens.map(aplicar) }));
    setAuditoriaSelecionada(prev =>
      (!prev || prev.auditoria_id !== auditoria_id) ? prev
        : { ...prev, itens: prev.itens.map(aplicar) });
  };

  // Admin vê o histórico completo; demais operadores veem as auditorias das
  // empresas sob sua responsabilidade (operador_empresa) — que são as que
  // precisam resolver — além das que eles mesmos executaram (operador).
  // Casa por PRIMEIRO NOME: o login vem como "Regis" e o operador da empresa
  // pode estar como "Regis Monteiro" — comparar o 1º token evita o descasamento.
  const minhasAuditorias = useMemo(() => {
    if (ehAdm) return auditorias;
    const primeiroNome = (s) => (s || '').trim().toLowerCase().split(/\s+/)[0];
    const u = primeiroNome(username);
    return auditorias.filter(a =>
      u && (primeiroNome(a.operador_empresa) === u || primeiroNome(a.operador) === u));
  }, [auditorias, username, ehAdm]);

  // "22/06/2026 18:45" -> "22/06/2026" (só a data, sem hora)
  const soData = (dt) => (dt || '').split(' ')[0];

  const competencias = useMemo(() => [...new Set(minhasAuditorias.map(a => a.periodo))].filter(Boolean).sort(), [minhasAuditorias]);
  const datasGeracao = useMemo(() => [...new Set(minhasAuditorias.map(a => soData(a.data_auditoria)))].filter(Boolean).sort(), [minhasAuditorias]);
  const operadores   = useMemo(() => [...new Set(minhasAuditorias.map(a => a.operador_empresa))].filter(Boolean).sort(), [minhasAuditorias]);

  const filtradas = useMemo(() => {
    const b = filtros.busca.toLowerCase();
    return minhasAuditorias.filter(a => {
      if (b && !a.cliente?.toLowerCase().includes(b) && !(a.numero_dominio || '').includes(b)) return false;
      if (filtros.operador && a.operador_empresa !== filtros.operador) return false;
      if (filtros.competencia && a.periodo !== filtros.competencia) return false;
      if (filtros.dataGeracao && soData(a.data_auditoria) !== filtros.dataGeracao) return false;
      if (filtros.apenasContestadas && !(a.tem_contestado && !a.resolvido)) return false;
      return true;
    });
  }, [minhasAuditorias, filtros]);

  // Todos os itens das auditorias filtradas, um por linha, já com o que a
  // linha precisa mostrar: dados da auditoria, dias em aberto e se o item é
  // recorrente (apontado em auditoria anterior do mesmo cliente).
  const itensPlanos = useMemo(() => {
    // chaves por cliente, em ordem cronológica, para marcar recorrência
    const porCliente = {};
    const paraData = (dt) => {
      const [d, m, y] = (dt || '').split(' ')[0].split('/').map(Number);
      return (d && m && y) ? new Date(y, m - 1, d).getTime() : 0;
    };
    auditorias.forEach(a => {
      const nd = a.numero_dominio || a.cliente;
      (porCliente[nd] = porCliente[nd] || []).push(a);
    });
    Object.values(porCliente).forEach(lst => lst.sort((x, y) => paraData(x.data_auditoria) - paraData(y.data_auditoria)));
    const anterioresDe = (a) => {
      const lst = porCliente[a.numero_dominio || a.cliente] || [];
      const set = new Set();
      for (const outra of lst) {
        if (outra.auditoria_id === a.auditoria_id) break;
        (outra.itens || []).forEach(it => set.add(chaveItem(it)));
      }
      return set;
    };

    const linhas = [];
    filtradas.forEach(a => {
      const anteriores = anterioresDe(a);
      const dias = diasEmAberto(a.data_auditoria);
      (a.itens || []).forEach(it => {
        linhas.push({
          ...it,
          _audit: a,
          _dias: dias,
          _recorrente: anteriores.has(chaveItem(it)),
          _situacao: it.resolvido ? 'resolvido' : it.em_andamento ? 'andamento' : 'pendente',
        });
      });
    });

    const b = filtroItem.texto.trim().toLowerCase();
    // "dd/mm/aaaa hh:mm" -> "aaaa-mm-dd", comparável direto com o valor do <input type="date">
    const isoData = (dt) => {
      const [d, m, y] = (dt || '').split(' ')[0].split('/');
      return (d && m && y) ? `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}` : '';
    };
    const pesoSev = { GRAVE: 0, MEDIO: 1, BAIXO: 2 };
    const pesoSit = { pendente: 0, andamento: 1, resolvido: 2 };
    return linhas
      .filter(it => {
        const dia = isoData(it._audit.data_auditoria);
        if (filtroItem.dataDe && dia && dia < filtroItem.dataDe) return false;
        if (filtroItem.dataAte && dia && dia > filtroItem.dataAte) return false;
        if (filtroItem.severidade && (it.severidade || '').toUpperCase() !== filtroItem.severidade) return false;
        // conferência do operador: confirmado, contestado ou ainda sem resposta
        if (filtroItem.conferencia) {
          const conf = (it.conferencia || '').toLowerCase();
          if (filtroItem.conferencia === 'sem' ? (conf === 'confirmado' || conf === 'contestado') : conf !== filtroItem.conferencia) return false;
        }
        if (filtroItem.situacao === 'pendentes' && it._situacao === 'resolvido') return false;
        if (filtroItem.situacao === 'abertos' && it._situacao !== 'pendente') return false;
        if (filtroItem.situacao === 'resolvidos' && it._situacao !== 'resolvido') return false;
        if (b) {
          const alvo = `${it.categoria || ''} ${it.conta || ''} ${it.descricao || ''} ${it.recomendacao || ''}`.toLowerCase();
          if (!alvo.includes(b)) return false;
        }
        return true;
      })
      // o que exige ação primeiro: pendente > em andamento > resolvido; dentro
      // disso o mais grave e, depois, o que está aberto há mais tempo
      .sort((x, y) =>
        (pesoSit[x._situacao] - pesoSit[y._situacao])
        || ((pesoSev[(x.severidade || '').toUpperCase()] ?? 9) - (pesoSev[(y.severidade || '').toUpperCase()] ?? 9))
        || ((y._dias || 0) - (x._dias || 0)));
  }, [filtradas, auditorias, filtroItem]);

  const resumoItens = useMemo(() => {
    const r = { total: itensPlanos.length, graves: 0, medios: 0, baixos: 0, pendentes: 0, andamento: 0, recorrentes: 0, contestados: 0, confirmados: 0 };
    itensPlanos.forEach(it => {
      const sv = (it.severidade || '').toUpperCase();
      if (sv === 'GRAVE') r.graves++; else if (sv === 'MEDIO') r.medios++; else if (sv === 'BAIXO') r.baixos++;
      if (it._situacao === 'pendente') r.pendentes++;
      if (it._situacao === 'andamento') r.andamento++;
      if (it._recorrente) r.recorrentes++;
      const conf = (it.conferencia || '').toLowerCase();
      if (conf === 'contestado') r.contestados++;
      if (conf === 'confirmado') r.confirmados++;
    });
    return r;
  }, [itensPlanos]);

  if (carregando) {
    return (
      <div className="das-page">
        <div className="container-fluid mt-5 text-center">
          <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }}></div>
          <p className="mt-3 text-muted">Carregando histórico de auditorias...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="das-page" style={{ zoom: 1.1 }}>
      <div className="container-fluid mt-4">

        {/* ── Header ── */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="mb-4">
                      <span className="badge bg-light text-dark me-2" style={{ fontSize: '20px', fontWeight: '500' }}>
                        Módulo de Auditoria Contábil
                      </span>
                      <span className="badge bg-warning text-dark" style={{ fontSize: '15px', fontWeight: '500' }}>
                        <i className="fas fa-history me-1"></i>
                        Histórico
                      </span>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <i className="fas fa-history header-icon me-2"></i>
                      <h2 className="titulo-pagina m-0">Histórico de Auditorias</h2>
                    </div>
                    <p className="mb-1 opacity-75">
                      <i className="fas fa-database me-2"></i>
                      {ehAdm
                        ? 'Registro completo de todas as auditorias contábeis realizadas (todos os operadores)'
                        : 'Registro das suas auditorias contábeis realizadas'}
                    </p>
                  </div>
                  <div className="mb-2">
                    <button
                      className="btn btn-sm"
                      onClick={() => window.history.back()}
                      style={{ backgroundColor: '#00205b', borderColor: '#00205b', color: '#fff' }}
                    >
                      <i className="fas fa-arrow-left me-1"></i>Voltar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {erro && (
          <div className="alert alert-danger alert-dismissible mb-4">
            <i className="fas fa-exclamation-circle me-2"></i>{erro}
            <button className="btn-close" onClick={() => setErro(null)}></button>
          </div>
        )}

        {/* ── Filtros ── */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white">
            <h6 className="mb-0">
              <i className="fas fa-filter me-2 text-primary"></i>Filtros
            </h6>
          </div>
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-3">
                <label className="form-label fw-semibold small">Buscar cliente / Nº domínio</label>
                <input type="text" className="form-control form-control-sm"
                  placeholder="Digite o nome ou número..."
                  value={filtros.busca}
                  onChange={e => setFiltros(f => ({ ...f, busca: e.target.value }))} />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold small">Operador da empresa</label>
                <select className="form-select form-select-sm" value={filtros.operador}
                  onChange={e => setFiltros(f => ({ ...f, operador: e.target.value }))}>
                  <option value="">Todos</option>
                  {operadores.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label fw-semibold small">Data da Auditoria</label>
                <select className="form-select form-select-sm" value={filtros.dataGeracao}
                  onChange={e => setFiltros(f => ({ ...f, dataGeracao: e.target.value }))}>
                  <option value="">Todas</option>
                  {datasGeracao.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label fw-semibold small">Competência</label>
                <select className="form-select form-select-sm" value={filtros.competencia}
                  onChange={e => setFiltros(f => ({ ...f, competencia: e.target.value }))}>
                  <option value="">Todas</option>
                  {competencias.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="col-md-2 d-flex flex-column gap-2">
                <button
                  className={`btn btn-sm w-100 ${filtros.apenasContestadas ? 'btn-danger' : 'btn-outline-danger'}`}
                  onClick={() => setFiltros(f => ({ ...f, apenasContestadas: !f.apenasContestadas }))}
                >
                  <i className="fas fa-clock me-1"></i>
                  {filtros.apenasContestadas ? 'Todas' : 'Pendentes'}
                </button>
                {(filtros.busca || filtros.operador || filtros.competencia || filtros.dataGeracao || filtros.apenasContestadas) && (
                  <button className="btn btn-sm btn-outline-secondary w-100"
                    onClick={() => setFiltros({ busca: '', operador: '', competencia: '', dataGeracao: '', apenasContestadas: false })}>
                    <i className="fas fa-times me-1"></i>Limpar
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Tabela ── */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h6 className="mb-0">
              <i className={`fas ${visao === 'itens' ? 'fa-list-check' : 'fa-list'} me-2 text-secondary`}></i>
              {visao === 'itens'
                ? <>Itens auditados ({itensPlanos.length}) <span className="text-muted fw-normal small">em {filtradas.length} auditoria(s)</span></>
                : <>Auditorias Realizadas ({filtradas.length})</>}
            </h6>
            <div className="d-flex align-items-center gap-2">
              {visao === 'auditorias' && <small className="text-muted me-2">Clique em uma linha para ver os itens auditados</small>}
              <div className="btn-group btn-group-sm" role="group" aria-label="Forma de exibição">
                <button type="button" className={`btn ${visao === 'itens' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => trocarVisao('itens')} title="Todos os itens auditados, sem abrir cada auditoria">
                  <i className="fas fa-list-check me-1"></i>Por item
                </button>
                <button type="button" className={`btn ${visao === 'auditorias' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => trocarVisao('auditorias')} title="Uma linha por auditoria">
                  <i className="fas fa-layer-group me-1"></i>Por auditoria
                </button>
              </div>
            </div>
          </div>

          {/* ── Visão por item: filtros próprios + resumo ── */}
          {visao === 'itens' && (
            <div className="px-3 py-2 border-bottom bg-light d-flex flex-wrap align-items-center gap-2">
              <div className="btn-group btn-group-sm" role="group" aria-label="Situação do item">
                {[['pendentes', 'Pendentes + em andamento'], ['abertos', 'Só pendentes'], ['resolvidos', 'Resolvidos'], ['todos', 'Todos']].map(([v, l]) => (
                  <button key={v} type="button"
                    className={`btn ${filtroItem.situacao === v ? 'btn-dark' : 'btn-outline-dark'}`}
                    onClick={() => setFiltroItem(f => ({ ...f, situacao: v }))}>{l}</button>
                ))}
              </div>
              <select className="form-select form-select-sm" style={{ width: 'auto' }} value={filtroItem.severidade}
                onChange={e => setFiltroItem(f => ({ ...f, severidade: e.target.value }))}>
                <option value="">Todas as severidades</option>
                <option value="GRAVE">Grave</option>
                <option value="MEDIO">Médio</option>
                <option value="BAIXO">Baixo</option>
              </select>
              <select className="form-select form-select-sm" style={{ width: 'auto' }} value={filtroItem.conferencia}
                title="Conferência do operador sobre o apontamento"
                onChange={e => setFiltroItem(f => ({ ...f, conferencia: e.target.value }))}>
                <option value="">Conferência: todas</option>
                <option value="confirmado">Confirmado</option>
                <option value="contestado">Contestado</option>
                <option value="sem">Sem conferência</option>
              </select>
              <div className="d-flex align-items-center gap-1" title="Data da auditoria">
                <span className="text-muted small">De</span>
                <input type="date" className="form-control form-control-sm" style={{ width: 150 }}
                  value={filtroItem.dataDe} max={filtroItem.dataAte || undefined}
                  onChange={e => setFiltroItem(f => ({ ...f, dataDe: e.target.value }))} />
                <span className="text-muted small">até</span>
                <input type="date" className="form-control form-control-sm" style={{ width: 150 }}
                  value={filtroItem.dataAte} min={filtroItem.dataDe || undefined}
                  onChange={e => setFiltroItem(f => ({ ...f, dataAte: e.target.value }))} />
                {(filtroItem.dataDe || filtroItem.dataAte) && (
                  <button type="button" className="btn btn-sm btn-outline-secondary" title="Limpar datas"
                    onClick={() => setFiltroItem(f => ({ ...f, dataDe: '', dataAte: '' }))}>
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
              <input type="text" className="form-control form-control-sm" style={{ width: 260 }}
                placeholder="Buscar em categoria, conta ou descrição…"
                value={filtroItem.texto} onChange={e => setFiltroItem(f => ({ ...f, texto: e.target.value }))} />
              <div className="ms-auto d-flex flex-wrap gap-1 small">
                <span className="badge bg-danger">{resumoItens.graves} grave(s)</span>
                <span className="badge bg-warning text-dark">{resumoItens.medios} médio(s)</span>
                <span className="badge bg-info text-dark">{resumoItens.baixos} baixo(s)</span>
                <span className="badge bg-secondary">{resumoItens.pendentes} pendente(s)</span>
                <span className="badge text-white" style={{ backgroundColor: '#fd7e14' }}>{resumoItens.andamento} em andamento</span>
                {resumoItens.confirmados > 0 && <span className="badge bg-success">{resumoItens.confirmados} confirmado(s)</span>}
                {resumoItens.contestados > 0 && <span className="badge bg-danger">{resumoItens.contestados} contestado(s)</span>}
                {resumoItens.recorrentes > 0 && (
                  <span className="badge" style={{ backgroundColor: '#6f42c1' }} title="Item já apontado em auditoria anterior do mesmo cliente">
                    <i className="fas fa-history me-1"></i>{resumoItens.recorrentes} recorrente(s)
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="card-body p-0">
            {visao === 'itens' ? (
              itensPlanos.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-clipboard-check fa-3x text-muted mb-3 d-block" style={{ opacity: 0.3 }}></i>
                  <h5 className="text-muted">Nenhum item para mostrar</h5>
                  <p className="text-muted small mb-0">Ajuste os filtros acima — por padrão aparecem só itens pendentes ou em andamento.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Cliente</th>
                        <th>Compet.</th>
                        <th>Operador</th>
                        <th className="text-center">Severidade</th>
                        <th>Categoria</th>
                        <th>Conta</th>
                        <th className="text-end">Valor</th>
                        <th style={{ minWidth: 220 }}>Descrição</th>
                        <th style={{ minWidth: 180 }}>Recomendação</th>
                        <th className="text-center">Situação</th>
                        <th className="text-center">Dias</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {itensPlanos.map(it => {
                        const a = it._audit;
                        const urgente = it._situacao !== 'resolvido' && it._dias != null && it._dias >= LIMITE_URGENTE;
                        return (
                          <tr key={`${a.auditoria_id}-${it.id}`} style={
                            it._situacao === 'resolvido' ? { background: '#f0fff4' }
                              : it._situacao === 'andamento' ? { background: '#fff8f0' } : {}
                          }>
                            <td style={{ fontSize: '0.83rem' }}>
                              <div className="fw-semibold">{a.cliente || '—'}</div>
                              <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                                <code style={{ fontSize: '0.72rem' }}>{a.numero_dominio || '—'}</code> · {soData(a.data_auditoria)}
                              </div>
                            </td>
                            <td className="text-nowrap" style={{ fontSize: '0.83rem' }}>{a.periodo || '—'}</td>
                            <td style={{ fontSize: '0.83rem' }}>{a.operador_empresa || '—'}</td>
                            <td className="text-center">
                              <SevBadge sev={it.severidade} />
                              {it.item_checklist === 'M' && <span className="badge bg-secondary ms-1" style={{ fontSize: '0.68rem' }}>Manual</span>}
                            </td>
                            <td style={{ fontSize: '0.83rem' }}>{it.categoria || '—'}</td>
                            <td className="fw-semibold" style={{ fontSize: '0.83rem' }}>
                              {it.conta || '—'}
                              {it._recorrente && (
                                <i className="fas fa-history ms-2" style={{ color: '#6f42c1', cursor: 'help' }}
                                  title="Já apontado em auditoria anterior deste cliente e, pelo visto, ainda não resolvido."></i>
                              )}
                            </td>
                            <td className="text-end text-nowrap" style={{ fontSize: '0.83rem' }}>{it.valor != null ? fmt(it.valor) : '—'}</td>
                            <td className="text-muted" style={{ fontSize: '0.8rem' }}>{it.descricao || '—'}</td>
                            <td className="text-muted fst-italic" style={{ fontSize: '0.8rem' }}>{it.recomendacao || '—'}</td>
                            <td className="text-center">
                              {it._situacao === 'resolvido' ? (
                                <span className="badge bg-success" title={it.resolvido_obs || ''}><i className="fas fa-check me-1"></i>Resolvido</span>
                              ) : it._situacao === 'andamento' ? (
                                <span className="badge text-white" style={{ backgroundColor: '#fd7e14' }} title={it.andamento_obs || ''}>
                                  <i className="fas fa-hourglass-half me-1"></i>Em andamento
                                </span>
                              ) : (
                                <span className="badge bg-secondary"><i className="fas fa-clock me-1"></i>Pendente</span>
                              )}
                              {['confirmado', 'contestado'].includes((it.conferencia || '').toLowerCase()) && (
                                <div className="mt-1"><ConferBadge val={it.conferencia} /></div>
                              )}
                            </td>
                            <td className={`text-center text-nowrap ${urgente ? 'fw-bold text-danger' : 'text-muted'}`} style={{ fontSize: '0.83rem' }}
                              title={urgente ? `Aberto há ${it._dias} dias — acima de ${LIMITE_URGENTE}` : ''}>
                              {it._situacao === 'resolvido' ? '—' : (it._dias != null ? `${it._dias}d` : '—')}
                            </td>
                            <td className="text-end text-nowrap">
                              <button className="btn btn-sm btn-outline-primary" title="Abrir a auditoria completa (resolver, plano, andamento)"
                                style={{ padding: '0.1rem 0.5rem', fontSize: '0.75rem' }}
                                onClick={() => setAuditoriaSelecionada(a)}>
                                Abrir
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            ) : filtradas.length === 0 ? (
              <div className="text-center py-5">
                <i className="fas fa-inbox fa-3x text-muted mb-3 d-block" style={{ opacity: 0.3 }}></i>
                <h5 className="text-muted">Nenhuma auditoria encontrada</h5>
                <p className="text-muted small mb-0">
                  {auditorias.length > 0
                    ? 'Tente ajustar os filtros.'
                    : 'Salve uma auditoria na página de resultado para ela aparecer aqui.'}
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Data da Auditoria</th>
                      <th>Cliente</th>
                      <th>Nº Domínio</th>
                      <th>Competência</th>
                      <th>Regime</th>
                      <th>Operador da Empresa</th>
                      <th className="text-center">Resolvido</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtradas.map(a => {
                      return (
                        <tr key={a.auditoria_id} style={{ cursor: 'pointer' }}
                          onClick={() => setAuditoriaSelecionada(a)}>
                          <td className="text-nowrap" style={{ fontSize: '0.83rem' }}>{a.data_auditoria}</td>
                          <td className="fw-semibold" style={{ fontSize: '0.85rem' }}>{a.cliente || '—'}</td>
                          <td><code style={{ fontSize: '0.80rem' }}>{a.numero_dominio || '—'}</code></td>
                          <td style={{ fontSize: '0.83rem' }}>{a.periodo || '—'}</td>
                          <td style={{ fontSize: '0.83rem' }}>{a.regime || '—'}</td>
                          <td style={{ fontSize: '0.83rem' }}>
                            <i className="fas fa-user-tie me-1 text-muted" style={{ fontSize: '0.75rem' }}></i>
                            {a.operador_empresa || '—'}
                          </td>
                          <td className="text-center">
                            {a.resolvido ? (
                              <span className="badge bg-success" style={{ fontSize: '0.75rem' }}>
                                <i className="fas fa-check me-1"></i>Resolvido
                              </span>
                            ) : a.pendente === false ? (
                              // nada mais a cobrar agora: o que falta está em
                              // acompanhamento e volta na próxima competência
                              <span className="badge text-white" style={{ fontSize: '0.75rem', backgroundColor: '#fd7e14' }}
                                title="Itens restantes estão em acompanhamento (aguardando cliente) — voltam na próxima competência">
                                <i className="fas fa-hourglass-half me-1"></i>Em andamento
                              </span>
                            ) : (
                              <span className="badge bg-secondary" style={{ fontSize: '0.75rem' }}>
                                <i className="fas fa-clock me-1"></i>Pendente
                                {a.em_andamento > 0 && (
                                  <span className="ms-1" style={{ fontSize: '0.68rem' }}>
                                    ({a.em_andamento} em and.)
                                  </span>
                                )}
                              </span>
                            )}
                          </td>
                          <td className="text-nowrap text-end">
                            <button className="btn btn-sm btn-outline-danger me-2"
                              title="Excluir esta auditoria"
                              style={{ padding: '0.1rem 0.4rem', fontSize: '0.75rem', lineHeight: 1.4 }}
                              onClick={e => {
                                e.stopPropagation();   // não abrir o modal de itens
                                setErroExcluir(null);
                                setAuditoriaParaExcluir(a);
                              }}>
                              <i className="fas fa-trash-alt"></i>
                            </button>
                            <i className="fas fa-chevron-right text-muted"></i>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

      </div>

      <ModalItens audit={auditoriaSelecionada} auditorias={auditorias} onClose={() => setAuditoriaSelecionada(null)}
        onToggleItem={handleToggleItem} onToggleAndamento={handleToggleAndamento}
        podeEditarPlano={podeEditarPlano} onEditarPlano={handleEditarPlano} />

      <PopupExcluir audit={auditoriaParaExcluir} excluindo={excluindo} erro={erroExcluir}
        onCancelar={() => { setAuditoriaParaExcluir(null); setErroExcluir(null); }}
        onConfirmar={handleExcluirAuditoria} />

      <style>{`.modal.show { display: block; }`}</style>
    </div>
  );
}
